/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { get, groupBy, isEmpty, keys, sumBy } from 'lodash'
import moment from 'moment'

import { centsToDollars } from '../../utils/formatters'

import RoundButton from '../RoundButton'
import RequestRefundModal from '../../containers/RequestRefundModal'

import styles from './Details.module.scss'
import { ReactComponent as ClearIcon } from '../../assets/clear-icon.svg'

import PeripheralBridge from '../../utils/peripheralBridge'

const OrderDetails = ({ history, onClearOrder, voidOrderFailed, voidOrderSucceeded, order, className }) => {
  const classNames = cn(styles.orderDetails, className)
  const itemModels = get(order, 'itemModels', [])
  const groupedItems = groupBy(itemModels, 'modifiersString')

  let totalCostInCents = 0
  itemModels.forEach(item => {
    let itemCost = item.priceInCents
    if (item.modifiers) {
      item.modifiers.forEach(modifier => {
        if (modifier.defaultPriceInCents > 0) {
          itemCost += modifier.defaultPriceInCents
        }
      })
    }
    totalCostInCents += itemCost
  });

  const [isVoiding, setIsVoiding] = useState(false)
  const [noBridge, setNoBridge] = useState(false)
  const [noCardTransaction, setNoCardTransaction] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)

  const formatTime = (time) => moment(time).format('h:mm:ss')

  useEffect(() => {
    setIsVoiding(false)
    setNoBridge(false)
    setNoCardTransaction(false)

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    bridgeInstance.registerHandler('handleResult', function(data, callback) {
      voidOrderSucceeded(order.id, data)
      setIsVoiding(false)
    })

    bridgeInstance.registerHandler('voidTransaction', function(data, callback) {
      voidOrderSucceeded(order.id, data)
      setIsVoiding(false)
    })
  }, [order])

  const onVoidOrder = () => {
    setIsVoiding(true)

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) {
      setIsVoiding(false)
      setNoBridge(true)
      return
    }

    const fccReqId = get(order, 'cardReaderData.requestId')
    if (!fccReqId) {
      setIsVoiding(false)
      setNoCardTransaction(true)
      return
    }

    bridgeInstance.call('voidTransactionAttempt', { 'requestId': fccReqId })
  }

  const isCardOrder = () => !isEmpty(order.cardReaderData)
  const isRichCheckoutOrder = () => get(order, 'isRichCheckout', false)
  const isCashOrder = () => !isCardOrder() && !isRichCheckoutOrder()
  const isOfflineCardApproval = () => isCardOrder() && !order.paymentCompleted
  const replayAuthDidFail = () => isOfflineCardApproval() && keys(order.replayStatus).length > 0
  const showVoidOptions = () => isCardOrder() && !voidDidSucceed() && !isOfflineCardApproval()
  const voidDidSucceed = () => !!order.voidedAt
  const voidDidFail = () => !!order.voidFailedAt

  const combineModifiers = (items) => {
    let lineItem = items[0]
    if (lineItem.modifiers) {
      return lineItem.modifiers.map((modifier) => {
        if (modifier.defaultPriceInCents > 0) {
          return(
            <div className={cn(styles.row, styles.modifierRow)}>
              <span>{items.length} {modifier.name}</span>
              <span>{centsToDollars(modifier.defaultPriceInCents * items.length)}</span>
            </div>
          )
        }
        return null
      })
    }
  }

  return (
    <div className={classNames}>
      <div className={styles.header}>
        <div className={styles.title}>{`Order # ${order.orderNumber}`}</div>
        <div className={styles.clearOrderIconContainer} onClick={onClearOrder}>
          <ClearIcon className={styles.clearOrderIcon} />
        </div>
      </div>

      <div className={styles.metadata}>
        <div className={styles.row}>
          <span>Created</span>
          <span>{formatTime(order.submittedAt)}</span>
        </div>
        <div className={styles.row}>
          <span>Synced</span>
          <span>{order.syncedAt ? formatTime(order.syncedAt) : '-'}</span>
        </div>
      </div>

      <div className={styles.lineItems}>
        {keys(groupedItems).map((itemId) => {
          const items = groupedItems[itemId]
          const name = (items[0] || {}).name
          const quantity = items.length
          const price = centsToDollars(sumBy(items, 'priceInCents'))
          return(

            <div className={styles.comboRow} key={itemId}>
              <div className={styles.row}>
                <span>{`${quantity} ${name}`}</span>
                <span>{price}</span>
              </div>

              {combineModifiers(items)}

            </div>
          )
        })}
      </div>

      <div className={styles.total}>
        <div className={styles.row}>
          <span>{`(${itemModels.length}) Subtotal`}</span>
          <span>{centsToDollars(totalCostInCents)}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.totalRow}>Total</span>
          <span className={styles.totalRow}>{centsToDollars(totalCostInCents)}</span>
        </div>
      </div>


      <div className={styles.orderHistoryContainer}>
        {isOfflineCardApproval() && ( <p className={styles.row}>- Credit card approved offline</p> )}

        {replayAuthDidFail() && (
          <p className={styles.row}>{`- ${order.replayStatus.statusDisplay}`}</p>
        )}

        {voidDidSucceed() && ( <p>{`Voided at ${formatTime(order.voidedAt)}`}</p> )}

        {voidDidFail() && ( <p>{`Unsuccessful void attempt at ${formatTime(order.voidFailedAt)}`}</p> )}

        {isCashOrder() && (<p className={styles.row}>- Paid with cash</p>)}
        {isRichCheckoutOrder() && (<p className={styles.row}>- Paid using the mobile app (no discounts are shown)</p>)}

        {order.errorMessage && (
          <p className={styles.row}>
            <span className={styles.errorMessage}>{`- Backend error: ${order.errorMessage}`}</span>
          </p>
        )}

        {showVoidOptions() && false && (
          <>
            {isVoiding && ( <p>Voiding...</p> )}

            {noBridge && ( <p>Couldn't connect to the card reader</p> )}

            {noCardTransaction && ( <p>No card transaction found to void</p> )}

            {!isVoiding && !noBridge && !noCardTransaction && (
              <RoundButton text="Void Payment" className={styles.voidButton} onClick={onVoidOrder} />
            )}
          </>
        )}
        <RoundButton text="View Receipt" className={styles.button} onClick={() => {
          history.push(`receipt/${order.uuid}`)
        }} />
        <RoundButton text={order.refundable ? "View Refund Request" : "Request Refund"} className={styles.button} onClick={() => setShowRefundModal(true)} />
        {showRefundModal && <RequestRefundModal order={order} edit={!order.refundable} onClose={() => setShowRefundModal(false)} />}
      </div>
    </div>
  )
}

OrderDetails.defaultProps = {
  order: {},
}

OrderDetails.propTypes = {
  onCloseDetails: PropTypes.func,
  voidOrderFailed: PropTypes.func,
  voidOrderSucceeded: PropTypes.func,
  order: PropTypes.object,
}

export default withRouter(OrderDetails)
